import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Target, Sparkles, Users, Search, Briefcase, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { fetchMyFunnel, trackAuthed } from "../lib/analytics";
import { toast } from "sonner";

const INTENT_ICONS = { CREATE_PROJECT: Sparkles, FIND_PROFESSIONALS: Search, MANAGE_PEOPLE: Users, OFFER_SERVICES: Briefcase };

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [step, setStep] = useState(0);
  const [intent, setIntent] = useState("");
  const [domain, setDomain] = useState("");
  const [profileType, setProfileType] = useState("");
  const [displayName, setDisplayName] = useState(user?.artist_name || "");
  const [mainGoal, setMainGoal] = useState("");
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [priorities, setPriorities] = useState([]);

  useEffect(() => { api.get("/catalog").then((r) => setCat(r.data)).catch(() => setCat({ intents: [], domains: [], profile_types: [] })); }, []);

  useEffect(() => {
    trackAuthed("onboarding_started");
    fetchMyFunnel().then((f) => {
      if (!f) return;
      if (f.intent) { setIntent(f.intent); setStep(1); }
      setPriorities(f.priorities || []);
      if (f.main_goal) setMainGoal(f.main_goal);
      else if ((f.priorities || []).length) setMainGoal(f.priorities[0]);
    });
  }, []);

  if (!cat) return <div className="min-h-screen bg-[#0A0A0C] flex justify-center items-center"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const domainProfiles = cat.profile_types.filter((p) => !domain || p.domain === domain);

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      const isMusic = profileType === "MUSIC_ARTIST";
      if (isMusic) {
        await api.post("/onboarding", {
          artist_name: displayName || user?.artist_name || "Artiste", genre: "Autre", current_level: "Débutant",
          main_goal: mainGoal || "Développer ma carrière", goal_description: bio || "",
          instagram: "", tiktok: "", youtube: "", spotify: "",
          timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })(),
        });
      }
      await api.post("/me/setup", { intent, domain, profile_type: profileType, display_name: displayName, bio, main_goal: mainGoal });
      await refresh();
      await trackAuthed("onboarding_completed", { intent, profile_type: profileType });
      toast.success("Profil créé. Bienvenue sur Cehnura !");
      if (intent === "OFFER_SERVICES") navigate("/pro");
      else if (intent === "MANAGE_PEOPLE") navigate("/become-manager");
      else navigate("/demarrage");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] px-4 py-10 flex justify-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Bienvenue</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">Configurons ton espace</h1>
          <p className="text-zinc-500 mt-2">Étape {step + 1} sur 3</p>
        </div>

        {priorities.length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="onb-diagnostic-priorities">
            <p className="text-xs uppercase tracking-wider text-amber-500 mb-2">D'après ton diagnostic</p>
            <ul className="space-y-1.5">
              {priorities.slice(0, 3).map((p, k) => (
                <li key={k} className="flex items-start gap-2 text-sm text-zinc-300"><Target className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />{p}</li>
              ))}
            </ul>
          </div>
        )}

        {step === 0 && (
          <div className="space-y-3" data-testid="onb-step-intent">
            <p className="font-display text-lg font-bold mb-2">Que souhaites-tu faire avec Cehnura ?</p>
            {cat.intents.map((it) => {
              const Icon = INTENT_ICONS[it.key] || Sparkles;
              return (
                <button key={it.key} onClick={() => { setIntent(it.key); setStep(1); }} data-testid={`onb-intent-${it.key}`}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${intent === it.key ? "border-amber-500/50 bg-amber-500/10" : "border-zinc-800 bg-[#18181E] hover:border-amber-500/40"}`}>
                  <span className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-black" /></span>
                  <span className="font-medium text-zinc-100">{it.label}</span>
                  <ArrowRight className="w-4 h-4 text-zinc-500 ml-auto" />
                </button>
              );
            })}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5 bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="onb-step-domain">
            <p className="font-display text-lg font-bold">Quel est ton domaine ?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {cat.domains.map((d) => (
                <button key={d.key} onClick={() => { setDomain(d.key); setProfileType(""); }} data-testid={`onb-domain-${d.key}`}
                  className={`h-11 rounded-xl text-sm border ${domain === d.key ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{d.label}</button>
              ))}
            </div>
            {domain && (
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">Ton type de profil</label>
                <div className="flex flex-wrap gap-2">
                  {domainProfiles.map((p) => (
                    <button key={p.key} onClick={() => setProfileType(p.key)} data-testid={`onb-ptype-${p.key}`}
                      className={`px-3 h-10 rounded-lg text-sm border ${profileType === p.key ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{p.label}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(0)} className="h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-zinc-300">Retour</button>
              <button onClick={() => setStep(2)} disabled={!domain || !profileType} data-testid="onb-domain-next" className="h-11 px-5 rounded-xl gold-gradient text-black font-semibold text-sm disabled:opacity-50">Continuer</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="onb-step-details">
            <p className="font-display text-lg font-bold">Parle-nous de toi</p>
            <Field label="Ton nom / nom d'artiste" value={displayName} onChange={setDisplayName} testid="onb-display-name" required />
            <Field label="Ton objectif principal" value={mainGoal} onChange={setMainGoal} testid="onb-goal" icon={Target} placeholder="Ex : Lancer mon projet dans 2 mois" />
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Décris ton projet / activité (optionnel)</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="onb-bio" rows={3}
                className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 outline-none resize-none" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="onb-error">{error}</p>}
            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(1)} className="h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-zinc-300">Retour</button>
              <button onClick={submit} disabled={loading || !displayName} data-testid="onb-submit" className="h-11 px-5 rounded-xl gold-gradient text-black font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accéder à mon espace"}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, icon: Icon, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />}
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} required={required}
          className={`w-full h-12 ${Icon ? "pl-10" : "px-4"} pr-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 outline-none`} />
      </div>
    </div>
  );
}
